import { useState, useEffect } from "react";
import { Smartphone, Share2, MoreHorizontal, Plus, Check, ArrowLeft } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";

const APP_URL = window.location.origin;
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(APP_URL)}&size=200x200&bgcolor=FFFFFF&color=0B1220&qzone=2`;

export default function InstallPage() {
  const [, navigate] = useLocation();
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone;

  useEffect(() => {
    if (isStandalone) setIsInstalled(true);
    const handler = (e: any) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-8 max-w-lg mx-auto">
      <div className="w-full mb-6">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>
      </div>

      <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
        <Smartphone className="w-8 h-8 text-primary" />
      </div>

      <h1 className="text-2xl font-bold text-foreground text-center mb-2">Installer Jobotech</h1>
      <p className="text-muted-foreground text-center text-sm mb-8 max-w-xs">
        Ajoutez l'application à votre écran d'accueil pour un accès instantané, même hors connexion.
      </p>

      {isInstalled ? (
        <div className="flex flex-col items-center gap-3 py-6">
          <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
            <Check className="w-7 h-7 text-green-500" />
          </div>
          <p className="font-semibold text-foreground">Application installée !</p>
          <p className="text-sm text-muted-foreground text-center">Jobotech est disponible sur votre écran d'accueil.</p>
        </div>
      ) : (
        <>
          {/* Android native install */}
          {deferredPrompt && (
            <Button onClick={handleInstall} className="w-full mb-6" size="lg">
              <Plus className="w-4 h-4 mr-2" />
              Installer l'application
            </Button>
          )}

          {/* iOS instructions */}
          {isIOS && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 mb-4">
              <h2 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
                <span className="text-base">📱</span> iPhone / iPad
              </h2>
              <ol className="space-y-3">
                {[
                  { icon: <Share2 className="w-4 h-4 text-blue-500 shrink-0" />, text: 'Appuyez sur le bouton Partager en bas de Safari' },
                  { icon: <Plus className="w-4 h-4 text-blue-500 shrink-0" />, text: '"Sur l\'écran d\'accueil"' },
                  { icon: <Check className="w-4 h-4 text-blue-500 shrink-0" />, text: 'Confirmez en appuyant sur "Ajouter"' },
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">{i + 1}</div>
                    <div className="flex items-center gap-2 mt-0.5">{step.icon}{step.text}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Android instructions (no native prompt) */}
          {isAndroid && !deferredPrompt && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 mb-4">
              <h2 className="font-semibold text-sm text-foreground mb-4 flex items-center gap-2">
                <span className="text-base">🤖</span> Android
              </h2>
              <ol className="space-y-3">
                {[
                  { icon: <MoreHorizontal className="w-4 h-4 text-blue-500 shrink-0" />, text: 'Appuyez sur le menu ⋮ en haut à droite' },
                  { icon: <Plus className="w-4 h-4 text-blue-500 shrink-0" />, text: '"Ajouter à l\'écran d\'accueil"' },
                  { icon: <Check className="w-4 h-4 text-blue-500 shrink-0" />, text: 'Confirmez en appuyant sur "Ajouter"' },
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">{i + 1}</div>
                    <div className="flex items-center gap-2 mt-0.5">{step.icon}{step.text}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Desktop QR code */}
          {!isIOS && !isAndroid && (
            <div className="w-full bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-4">
              <h2 className="font-semibold text-sm text-foreground">Ouvrir sur mobile</h2>
              <img src={QR_URL} alt="QR Code Jobotech" className="w-48 h-48 rounded-xl" />
              <p className="text-xs text-muted-foreground text-center">Scannez ce QR code avec votre smartphone pour ouvrir l'application sur mobile</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
